<template>
  <div class="pdf-splitter">
    <div class="split-header">
      <h2 class="tool-title">{{ $t('split.title') }}</h2>
      <p class="tool-description">{{ $t('split.description') }}</p>
    </div>

    <div class="card">
      <FileDropZone
        :text="$t('split.selectFile')"
        :subtext="$t('split.orDrag')"
        v-model="selectedFile"
        @file-loaded="handleFileLoaded"
      />

      <div v-if="selectedFile" class="split-options">
        <div class="option-section">
          <label class="option-label">{{ $t('split.pageRanges') }}</label>

          <div class="range-inputs">
            <input
              v-model="pageRanges"
              type="text"
              class="input-field"
              :placeholder="$t('split.pageRangesPlaceholder')"
              :disabled="splitAll"
            />

            <button
              @click="splitAll = !splitAll"
              class="toggle-btn"
              :class="{ active: splitAll }"
            >
              {{ $t('split.splitAll') }}
            </button>
          </div>

          <p v-if="!splitAll" class="help-text">{{ $t('split.pageRangesHelp') }}</p>
        </div>

        <div class="action-section">
          <button
            @click="handleSplit"
            :disabled="loading || (!splitAll && !pageRanges.trim())"
            class="btn-primary w-full"
          >
            <span v-if="loading">{{ $t('split.processing') }}</span>
            <span v-else>{{ $t('split.splitButton') }}</span>
          </button>
        </div>

        <div v-if="error" class="error-message">
          {{ error }}
        </div>

        <div v-if="success" class="success-message">
          {{ success }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import FileDropZone from './FileDropZone.vue'
import { usePdfHandler } from '@/composables/usePdfHandler'

const selectedFile = ref<File | null>(null)
const pageRanges = ref('')
const splitAll = ref(false)
const fileInfo = ref<{ pageCount: number; size: string } | null>(null)

const { loading, error, splitPdf, downloadMultiplePdfs } = usePdfHandler()
const success = ref<string | null>(null)

function handleFileLoaded(info: { pageCount: number; size: string }) {
  fileInfo.value = info
  success.value = null
}

async function handleSplit() {
  if (!selectedFile.value) return

  try {
    success.value = null
    const blobs = await splitPdf(selectedFile.value, pageRanges.value, splitAll.value)

    if (blobs.length === 1) {
      // Download single file
      const timestamp = new Date().toISOString().slice(0, 10)
      downloadMultiplePdfs(blobs, `split_${timestamp}`)
    } else {
      // Download multiple files
      const timestamp = new Date().toISOString().slice(0, 10)
      downloadMultiplePdfs(blobs, `split_${timestamp}`)
    }

    success.value = $t('split.success')
  } catch (err) {
    console.error('Split error:', err)
  }
}
</script>

<style scoped>
.pdf-splitter {
  @apply max-w-2xl mx-auto;
}

.split-header {
  @apply mb-6 text-center;
}

.tool-title {
  @apply text-3xl font-bold text-gray-900 mb-2;
}

.tool-description {
  @apply text-gray-600;
}

.split-options {
  @apply mt-6 space-y-6;
}

.option-section {
  @apply space-y-3;
}

.option-label {
  @apply block text-sm font-medium text-gray-700;
}

.range-inputs {
  @apply flex gap-3;
}

.range-inputs input {
  flex: 1;
}

.toggle-btn {
  @apply px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap;
}

.toggle-btn.active {
  @apply bg-indigo-100 text-indigo-700;
}

.help-text {
  @apply text-sm text-gray-500;
}

.action-section {
  @apply pt-4 border-t border-gray-200;
}

.error-message {
  @apply p-4 bg-red-50 text-red-700 rounded-lg;
}

.success-message {
  @apply p-4 bg-green-50 text-green-700 rounded-lg;
}

.btn-primary:disabled {
  @apply opacity-50 cursor-not-allowed;
}
</style>
